<script lang="ts">
	import { signOut } from '$lib/authentication/firebase'
	import { getContextClient } from '@urql/svelte'
	import SidebarItem from './SidebarItem.svelte'
	import SidebarAccountMenu from './SidebarAccountMenu.svelte'
	import {
		InsertTripDocument,
		type InsertTripMutation,
		type InsertTripMutationVariables,
	} from '$src/lib/queries/generated/InsertTrip'
	import { goto } from '$app/navigation'
	import LocationPinIcon from '../icons/LocationPinIcon.svelte'
	import AddIcon from '../icons/AddIcon.svelte'
	import HelpChatIcon from '../icons/HelpChatIcon.svelte'
	import { page } from '$app/stores'
	import ShopIcon from '../icons/ShopIcon.svelte'
	import { track } from '$lib/utils/track'
	import authState from '$lib/stores/auth-state'
	import { clearInsertTripOnLoadParams, loginAndInsertTrip } from '$lib/utils/insertTrip'
	import { performSignIn } from '$lib/authentication/helpers'
	import LoginIcon from '../icons/LoginIcon.svelte'
	import modal from '$lib/stores/modal-state'
	import CompassIcon from '../icons/CompassIcon.svelte'

	let path = $derived($page.url.pathname)
	let signedIn = $derived($authState.signedIn)

	let currentHover: string | null = $state(null)

	function navigateToExplore() {
		const currentUrl = new URL(window.location.href)
		const url = new URL('/explore', window.location.origin)

		const fullscreenParam = currentUrl.searchParams.get('fullscreen')
		if (fullscreenParam !== null) {
			url.searchParams.set('fullscreen', fullscreenParam)
		}

		goto(url.toString())
	}

	function navigateToTrips() {
		const currentUrl = new URL(window.location.href)
		const url = new URL('/trips', window.location.origin)

		const fullscreenParam = currentUrl.searchParams.get('fullscreen')
		if (fullscreenParam !== null) {
			url.searchParams.set('fullscreen', fullscreenParam)
		}

		goto(url.toString())
	}

	function handleStartTripClick() {
		track('Start Trip Clicked')
		if (signedIn) {
			handleInsertTrip()
		} else {
			loginAndInsertTrip(
				performSignIn,
				() => handleInsertTrip().then(clearInsertTripOnLoadParams),
				null,
			)

			return Promise.resolve()
		}
	}

	function handleSignIn(event: MouseEvent) {
		event.preventDefault()
		track('Sign In Clicked')
		modal.open('signup', { intent: 'signIn' })
	}

	const client = getContextClient()
	const insertTripMutation = (vars: InsertTripMutationVariables) =>
		client
			.mutation<InsertTripMutation, InsertTripMutationVariables>(InsertTripDocument, vars)
			.toPromise()

	function handleSignOut() {
		track('Sign Out Clicked')
		signOut()
	}

	async function handleInsertTrip() {
		insertTripMutation({ input: { title: 'Add a Name' } })
			.then((res) => {
				if (res?.data?.insertTrip?.id) goto(`/trips/${res?.data?.insertTrip?.id}`)
			})
			.catch((err) => {
				console.error(err)
			})
	}

	const openMailer = () => {
		track('Open Mailer', { category: 'Sidebar' })
		// chatLoader.loadChat({ open: true })
		var email = 'support@airheart.com'
		var subject = encodeURIComponent('Requesting your help')
		var body = encodeURIComponent('Describe your problem...')

		var mailtoLink = 'mailto:' + email + '?subject=' + subject + '&body=' + body
		window.location.href = mailtoLink
		// window.open('https://intercom.help/airheart/en', '_blank')
	}

	const openStore = () => {
		track('Store Clicked')

		const currentUrl = new URL(window.location.href)
		const url = new URL('/store', window.location.origin)

		const fullscreenParam = currentUrl.searchParams.get('fullscreen')
		if (fullscreenParam !== null) {
			url.searchParams.set('fullscreen', fullscreenParam)
		}

		goto(url.toString())
	}

	function handleHover(title: string | null) {
		currentHover = title
	}
</script>

<div
	class="hidden h-screen w-[72px] overflow-hidden lg:flex flex-col gap-y-5 lg:fixed dark:bg-brand-gray-6 bg-brand-gray-1 pb-4 z-20"
>
	<div class="flex mt-6 flex-shrink-0 items-center justify-center">
		<a href="/" class="items-center leading-none hidden lg:flex h-10">
			<img src="/logo-heart-gray.svg" height={40} width={40} alt="Airheart Logo" />
		</a>
	</div>
	<nav class="flex flex-1 flex-col">
		<ul role="list" class="flex justify-between flex-1 flex-col gap-y-7">
			<li>
				<ul role="list" class="px-2">
					<button onclick={navigateToExplore} class="w-full bg-transparent border-0 cursor-pointer">
						<SidebarItem title="Explore" onhover={handleHover} isActive={path.includes('/explore')}>
							<CompassIcon
								class={`h-5 w-5 transition-color group-hover:text-white ${
									path.includes('/explore') ? 'text-white' : 'text-brand-gray-4'
								}`}
							/>
						</SidebarItem>
					</button>

					<button onclick={navigateToTrips} class="w-full bg-transparent border-0 cursor-pointer">
						<SidebarItem title="Trips" onhover={handleHover} isActive={path.includes('/trips')}>
							<LocationPinIcon
								class={`h-6 w-6 transition-color group-hover:text-white ${
									path.includes('/trips') ? 'text-white' : 'text-brand-gray-4'
								}`}
							/>
						</SidebarItem>
					</button>
				</ul>

				<div class="w-[calc(100%-2rem)] bg-brand-gray-4 bg-opacity-50 h-[1px] mx-auto my-2">
					<!-- Save space -->
				</div>

				<ul role="list" class="mx-2 space-y-1">
					<button onclick={handleStartTripClick} class="w-full">
						<SidebarItem title="Trip" onhover={handleHover}>
							<AddIcon class="h-5 w-5 transition-color text-brand-gray-4 group-hover:text-white" />
						</SidebarItem>
					</button>
				</ul>
			</li>
			<li>
				<ul role="list" class="px-2 mt-2">
					<button onclick={openStore} class="w-full">
						<SidebarItem title="Store" onhover={handleHover} isActive={path.includes('/store')}>
							<ShopIcon
								class={`h-6 w-6 transition-color group-hover:text-white ${
									path.includes('/store') ? 'text-white' : 'text-brand-gray-4'
								}`}
							/>
						</SidebarItem>
					</button>
					<button onclick={openMailer} class="w-full">
						<SidebarItem title="Help" onhover={handleHover}>
							<HelpChatIcon
								class="h-5 w-5 transition-color text-brand-gray-4 group-hover:text-white"
							/>
						</SidebarItem>
					</button>
					{#if signedIn}
						<SidebarAccountMenu
							class="w-full"
							user={$authState.currentUser}
							onsignout={handleSignOut}
						/>
					{:else}
						<button onclick={handleSignIn} class="w-full">
							<SidebarItem title="Login" onhover={handleHover}>
								<LoginIcon
									class="h-6 w-6 transition-color text-brand-gray-4 group-hover:text-white"
								/>
							</SidebarItem>
						</button>
					{/if}
				</ul>
			</li>
		</ul>
	</nav>
</div>

<div class="hidden lg:block w-[72px] flex-shrink-0">&nbsp;</div>
